
import AppCustomTable from "@/components/common/CustomTable.vue";
import AppFullscreenContainer from "@/components/common/MobileFullScreenContainer.vue";
import AppToolbar from "@/components/common/ToolbarBase.vue";
import {Bank, Sale} from "@/models/base";
import {BankService} from "@/services/bank-service";
import {BookYearService} from "@/services/book-year-service";
import Vue from "vue";
import {CustomerService} from "../../services/customer-service";
import {UserConfigService} from "../../services/user-config-service";
import AppBankEditDefault from "./BankEditDefaultCard.vue";
import AppBankEditHistoryItem from "@/components/bank/BankEditHistoryItem.vue";
import {printHumanFloat} from "@/utils/pretty-print";
import {BankKind} from "@/models/base/bank";
import {DossierConfigService} from "@/services";
import AppExtraFieldsEdit from "./ExtraFieldsEdit.vue";

export default Vue.extend({
	data() {
		return {
			tab: 0,
			pendingChanges: false,
			balanceCheck: false,
			selectedTab: 0,
			historySeries: [] as Bank[],
			serieTotal: 0,
			batch: [] as any,
			clearBatch: 1,
			saving: false,
			originalBank: new Bank(),
			isDefaultValid: false,
			config: DossierConfigService.getBookkeepingConfig()
		};
	},
	props: {
		bank: Object as () => Bank,
		showEditComposition: Boolean,
	},
	computed: {
		locale(): string {
			return this.$i18n.locale;
		},
		tabs(): any {
			return this.$ct("tabs");
		},
		serieTotalString():string {
			return printHumanFloat(this.serieTotal);
		},
		isValid():boolean{
			return this.isDefaultValid;
		}
	},
	watch: {
		bank: {
			deep: true,
			handler(bank: Bank, oldBank: Bank) {
				if (oldBank != bank) return;
				this.pendingChanges = true;
			}
		}
	},
	methods: {
		beforeSave():boolean{
			if(this.bank.Kind == BankKind.Sale && this.bank.InvoiceID==0 && this.batch.length==0){
				this.$wf.notify(this.$ct("no-sale-selected").toString(), "danger", 3000, "exclamation-triangle");
				return false;
			}
			if(this.bank.Kind == BankKind.Purchase && this.bank.PurchaseID==0 && this.batch.length==0){
				this.$wf.notify(this.$ct("no-purchase-selected").toString(), "danger", 3000, "exclamation-triangle");
				return false;
			}
			if(this.bank.Kind == BankKind.Diverse && this.isCentralLedgerAccount(this.bank.LedgerAccountCode)){
				this.$wf.notify(this.$ct("diverse-on-central-not-allowed", {code: this.bank.LedgerAccountCode}).toString(), "danger", 5000, "exclamation-triangle");
				return false;
			}
			return true;
		},
		isCentralLedgerAccount(code:string | null):boolean{
			if (!code) return false;
			let config = DossierConfigService.getBookkeepingConfig();
			return code == config.DefaultSaleCentral || code == config.DefaultPurchaseCentral;
		},
		// eslint-disable-next-line max-lines-per-function
		async save(repeat: Boolean) {
			if (!this.isValid) return;
			if(!this.beforeSave()) return;
			if (this.saving) return;
			this.saving = true;
			try{
				if (!this.bank.BookYear){
					this.bank.BookYear = BookYearService.selectedBookYear;
				}

				if(this.batch.length>0){
					await this.saveBatch();
					return;
				}

				if (!BookYearService.isDateInBookYear(this.bank.TimeCreated, this.bank.BookYear)) {
					let message = this.$ct("bank-date-is-not-in-bookyear");
					return await this.$wf.dialog(message, this.$ct("common.Ok"), this.$ct("common.error"), "warning");
				}

				if (this.bank.ID != 0 && this.bank.ID != null) {
					var bank = await BankService.putBooking(this.bank);
				} else {
					var bank = await BankService.postBooking(this.bank);
				}

				this.$wf.notify(this.$ct("save-success").toString(), "success", 3000, "check");
				if (UserConfigService.getGeneralSettings().RepeatBankSave){
					bank = new Bank();
					this.$emit("bank-saved", bank);
					this.pendingChanges = false;
				}else if(!repeat){
					this.$emit("update");
					this.cancel();
				}
			}catch(err){

			}finally{
				this.saving = false;
			}
		},
		// eslint-disable-next-line max-lines-per-function
		async saveBatch(){
			let batch = [] as Bank[];
			let totalDc = 0;
			for (let s of this.batch) {
				let b = new Bank(this.bank);
				b.Contact = s.Contact;
				b.ContactID = s.ContactID;
				b.Total = s.TotalOpen;
				b.TotalDC = s.TotalOpen;

				if(b.Kind == 2){
					b.Invoice = s;
					b.FriendlySaleID = s.ComputedFriendlyID;
					b.InvoiceID = s.ID;
				}
				if(b.Kind == 3){
					b.Purchase = s;
					b.PurchaseID = s.ID;
					b.FriendlyPurchaseID = s.ComputedFriendlyID;
					b.TotalDC = s.TotalOpen*-1;
				}
				totalDc += b.TotalDC;
				batch.push(b);
			}
			await BankService.batchPostBooking(batch);
			this.batch = [];
			this.$wf.notify(this.$ct("save-success").toString(), "success", 3000, "check");
			//Delete original bank booking
			if(this.originalBank?.TotalDC == totalDc){
				await BankService.deleteBookings([this.originalBank]);
			}

			//Edit original bank booking
			if (this.originalBank && this.originalBank.ID && this.originalBank?.TotalDC != totalDc){
				this.originalBank.TotalDC = this.originalBank.TotalDC - totalDc;
				this.originalBank.Total = Math.abs(this.originalBank.TotalDC);
				this.originalBank.TypeID = 1;
				await BankService.putBooking(this.originalBank);
			}

			if (UserConfigService.getGeneralSettings().RepeatBankSave){
				let bank = new Bank();
				this.clearBatch ++;
				this.$emit("bank-saved", bank);
				this.pendingChanges = false;
			}else{
				await this.$router.push("/bank");
			}

		},
		async saveContinue() {
			if (!this.isValid) return;
			if(!this.beforeSave()) return;

			await this.save(true);

			this.historySeries.push(this.bank);
			this.serieTotal = this.serieTotal + this.bank?.TotalDC;

			let bank = new Bank();
			bank.StNr = this.bank.StNr;
			bank.TypeID = this.bank.TypeID;
			bank.TimeCreated = this.bank.TimeCreated;
			bank.Kind = this.bank.Kind;
			if(bank.Kind == BankKind.Diverse){
				bank.LedgerAccountCode = this.config.DefaultSuspenseAccount;
			}
			this.$emit("update:bank", bank);
		},
		cancel() {
			this.$emit("cancel");
		},
		onUpdateBatch(batch: Sale[]){
			this.batch = batch;
		}
	},
	async mounted() {
		if (this.bank.ContactID) {
			this.bank.Contact = await CustomerService.getContact(this.bank.ContactID);
		}
		this.originalBank = new Bank(this.bank.getJSON());
	},
	components: {
		AppFullscreenContainer,
		AppToolbar,
		AppCustomTable,
		AppBankEditDefault,
		AppBankEditHistoryItem,
		AppExtraFieldsEdit
	},
	i18nEx: {
		componentPrefix: "components.bank"
	}
});
